import classNames from 'classnames';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';

import { Box, Wrapper } from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Services
import { wrapperSize } from '@/services/utils/box';
import { applicationEnv } from '@/services/env/shared';

// Components
import { WithTable } from '@/slices/shared/Region/WithTable';
import { WithImage } from '@/slices/shared/Region/WithImage';
import { WithVimeo } from '@/slices/shared/Region/WithVimeo';
import { WithIcons } from '@/slices/shared/Region/WithIcons';
import { WithQuote } from '@/slices/shared/Region/WithQuote';
import { WithBrands } from '@/slices/shared/Region/WithBrands';
import { WithMarquee } from '@/slices/shared/Region/WithMarquee';
import { WithBrandImage } from '@/slices/shared/Region/WithBrandImage';
import { WithIconFeatures } from '@/slices/shared/Region/WithIconFeatures';
import { WithBrandFeatures } from '@/slices/shared/Region/WithBrandFeatures';
import { WithImageFeatures } from '@/slices/shared/Region/WithImageFeatures';
import { WithUnorderedList } from '@/slices/shared/Region/WithUnorderedList';
import { WithSimpleFeatures } from '@/slices/shared/Region/WithSimpleFeatures';
import { WithStatisticsFeatures } from '@/slices/shared/Region/WithStatisticsFeatures';
import { WithSimpleContactDetails } from '@/slices/shared/Region/WithSimpleContactDetails';

export type RegionProps = SliceComponentProps<Content.RegionSlice>;

const Region = ({ slice }: RegionProps) => {
  const { id: sliceId, variation, primary } = slice;
  const { wrapper_size, override_padding, identifier: sectionId } = primary;

  const { WebsiteUrl } = applicationEnv();

  return (
    <Box
      component="section"
      id={sectionId ?? undefined}
      className={classNames({
        'm-0':
          variation === 'withSimpleFeatures' && primary.remove_parent_region,
      })}
    >
      <Wrapper size={wrapperSize(wrapper_size)}>
        <Box
          addRegion
          component="div"
          addSwitcher={variation === 'withMarquee'}
          className={classNames({
            'pt-0': override_padding === 'No Padding - Top',
            'pb-0': override_padding === 'No Padding - Bottom',
            'py-0': override_padding === 'No Padding - Top Bottom',

            'region--video': variation === 'withVimeo',
            'region--quote': variation === 'withQuote',
            'region--icons': variation === 'withIcons',
            'region--brands': variation === 'withBrands',
            'region--marquee': variation === 'withMarquee',
            'region--heading': variation === 'withHeading',

            'region--features':
              variation === 'withIconFeatures' ||
              variation === 'withBrandFeatures' ||
              variation === 'withImageFeatures' ||
              variation === 'withSimpleFeatures',

            'p-0':
              variation === 'withSimpleFeatures' &&
              primary.remove_parent_region,
          })}
        >
          {variation === 'withTable' && (
            <WithTable sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withMarquee' && (
            <WithMarquee sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withSimpleContactDetails' && (
            <WithSimpleContactDetails sliceId={sliceId} slice={slice} />
          )}

          {(variation === 'default' || variation === 'withHeading') && (
            <Box component="div" addFlow>
              <RichText
                variant="default"
                websiteUrl={WebsiteUrl}
                content={primary.content}
              />
            </Box>
          )}

          {variation === 'withIcons' && (
            <WithIcons sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withBrands' && (
            <WithBrands sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withImageFeatures' && (
            <WithImageFeatures sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withBrandFeatures' && (
            <WithBrandFeatures sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withSimpleFeatures' && (
            <WithSimpleFeatures sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withIconFeatures' && (
            <WithIconFeatures sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withStatisticsFeatures' && (
            <WithStatisticsFeatures sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withUnorderedList' && (
            <WithUnorderedList sliceId={sliceId} slice={slice} />
          )}

          {variation === 'withVimeo' && <WithVimeo slice={slice} />}
          {variation === 'withImage' && <WithImage slice={slice} />}
          {variation === 'withQuote' && <WithQuote slice={slice} />}
          {variation === 'withBrandImage' && <WithBrandImage slice={slice} />}
        </Box>
      </Wrapper>
    </Box>
  );
};

export default Region;
